// "Compare against season" — the shared client pieces (2026-10-23, Kathryn Watson plan §1.5).
// Mirrors api/src/season.ts: a season is the NYMR operating year (1 March – end February);
// the last-season window is the same calendar dates a year earlier, or 364 days earlier
// ("align by week") so a Saturday compares with a Saturday.
import type { CSSProperties, ReactNode } from 'react'

export type CompareAlign = 'calendar' | 'week'
export const LY_GREY = '#8a8577'

const pad = (n: number) => String(n).padStart(2, '0')
const isoOf = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`

export function seasonLabelFor(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`)
  const y = d.getUTCMonth() + 1 >= 3 ? d.getUTCFullYear() : d.getUTCFullYear() - 1
  return `${y}/${y + 1}`
}
export function priorSeasonLabel(label: string): string {
  const m = label.match(/^(\d{4})\/(\d{4})$/)
  return m ? `${Number(m[1]) - 1}/${Number(m[2]) - 1}` : label
}
export function shortSeasonLabel(label: string): string {
  const m = label.match(/^(\d{4})\/(\d{4})$/)
  return m ? `${m[1]}/${m[2].slice(2)}` : label
}
export function lyDate(iso: string, align: CompareAlign = 'calendar'): string {
  const d = new Date(`${iso}T00:00:00Z`)
  if (align === 'week') return isoOf(new Date(d.getTime() - 364 * 86_400_000))
  const y = d.getUTCFullYear() - 1, m = d.getUTCMonth(), day = d.getUTCDate()
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate()
  return isoOf(new Date(Date.UTC(y, m, Math.min(day, last))))
}
export interface CompareWindows { from: string; to: string; ly_from: string; ly_to: string; season: string; ly_season: string; align: CompareAlign }
export function lyWindow(from: string, to: string, align: CompareAlign = 'calendar'): CompareWindows {
  const season = seasonLabelFor(from)
  return { from, to, ly_from: lyDate(from, align), ly_to: lyDate(to, align), season, ly_season: priorSeasonLabel(season), align }
}

const dMY = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
export function windowLabel(from: string, to: string): string { return from === to ? dMY(from) : `${dMY(from)} – ${dMY(to)}` }

/** Difference between a current and a last-season figure, formatted: "+12 (+8%)". */
export function deltaLabel(current: number, ly: number, fmt: (n: number) => string = (n) => n.toLocaleString('en-GB')): string {
  const d = current - ly
  const sign = d > 0 ? '+' : d < 0 ? '−' : ''
  const pct = ly !== 0 ? ` (${d > 0 ? '+' : d < 0 ? '−' : ''}${Math.round(Math.abs(d) / Math.abs(ly) * 100)}%)` : ''
  return `${sign}${fmt(Math.abs(d))}${pct}`
}

/** The header control: a toggle button, and when on, the alignment choice and the window it compares to. */
export function CompareToggle({ on, onToggle, align, onAlign, windows }: { on: boolean; onToggle: () => void; align: CompareAlign; onAlign: (a: CompareAlign) => void; windows: CompareWindows | null }) {
  const btn: CSSProperties = { font: 'inherit', fontSize: '0.85rem', fontWeight: 600, padding: '0.45rem 0.9rem', borderRadius: 4, cursor: 'pointer', border: '1px solid var(--nymr-green, #0b3d2e)', background: on ? 'var(--nymr-green, #0b3d2e)' : '#fff', color: on ? '#fff' : 'var(--nymr-green, #0b3d2e)' }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <button type="button" style={btn} aria-pressed={on} onClick={onToggle} title="Show last season's figures next to this season's for the same period">
        Compare to last season
      </button>
      {on && (
        <>
          <select value={align} onChange={(e) => onAlign(e.target.value as CompareAlign)} aria-label="How to line up last season"
            style={{ font: 'inherit', fontSize: '0.8rem', padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: 4, background: '#fff' }}
            title="Same dates: the same calendar dates a year earlier. Same weeks: 364 days earlier, so each weekday lines up.">
            <option value="calendar">Same dates</option>
            <option value="week">Same weeks</option>
          </select>
          {windows && <span style={{ fontSize: '0.78rem', color: LY_GREY }}>vs {windowLabel(windows.ly_from, windows.ly_to)} ({shortSeasonLabel(windows.ly_season)})</span>}
        </>
      )}
    </span>
  )
}

/** A grey last-season figure with its delta, for use under a KPI or in a table cell. */
export function LyCell({ current, ly, fmt, style }: { current: number; ly: number | null | undefined; fmt: (n: number) => string; style?: CSSProperties }): ReactNode {
  if (ly === null || ly === undefined) return <span style={{ color: LY_GREY, ...style }}>—</span>
  return <span style={{ color: LY_GREY, whiteSpace: 'nowrap', ...style }} title={`Last season ${fmt(ly)} · change ${deltaLabel(current, ly, fmt)}`}>{fmt(ly)} <small>{deltaLabel(current, ly, fmt)}</small></span>
}
